/**
 * Volume Role - Cast Pillar
 *
 * Normaliza input sujo para número tipado.
 * Detecta unidades em strings e converte se necessário.
 *
 * @example
 * import { castVolume, castVolumeStrict, detectVolumeUnit } from '@attrx/role-morphic/volume/cast';
 *
 * castVolume('500 ml', 'liter');       // 0.5 (converte)
 * castVolume('1 gal', 'liter');        // 3.785411784 (converte)
 * castVolumeStrict('500 ml');          // 500 (não converte)
 * detectVolumeUnit('2 gallons');       // 'gallon_us'
 */

import { VOLUME_UNITS, VOLUME_ALIASES, type VolumeUnit } from "./constants";
import { convertVolume } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, detectando e convertendo unidade se presente
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo para conversão
 * @returns Número ou null se não puder converter
 *
 * @example
 * castVolume(100, 'liter');              // 100
 * castVolume('100', 'liter');            // 100
 * castVolume('500 ml', 'liter');         // 0.5 (converte)
 * castVolume('1 gal', 'liter');          // 3.785411784 (converte)
 * castVolume('invalid', 'liter');        // null
 */
export function castVolume(
  input: unknown,
  targetVariant: VolumeUnit = "liter"
): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    return parseVolumeString(input, targetVariant);
  }

  return null;
}

/**
 * Faz cast sem conversão de unidade (só parseia o valor literal)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castVolumeStrict('500 ml');   // 500 (ignora unidade, não converte)
 * castVolumeStrict('100');      // 100
 * castVolumeStrict(100);        // 100
 */
export function castVolumeStrict(input: unknown): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    const numValue = parseNumber(input);
    return numValue;
  }

  return null;
}

/**
 * Versão safe de castVolume que retorna Result
 */
export function tryCastVolume(
  input: unknown,
  targetVariant: VolumeUnit = "liter"
): Result<number> {
  const result = castVolume(input, targetVariant);
  if (result === null) {
    return {
      ok: false,
      error: `Cannot cast "${String(input)}" to volume:${targetVariant}`,
    };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECTION
// =============================================================================

/**
 * Detecta unidade de volume a partir de uma string
 *
 * @param input - String com possível unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectVolumeUnit('500 ml');        // 'milliliter'
 * detectVolumeUnit('2 gallons');     // 'gallon_us'
 * detectVolumeUnit('1 L');           // 'liter'
 * detectVolumeUnit('100');           // null
 */
export function detectVolumeUnit(input: string): VolumeUnit | null {
  const trimmed = input.trim().toLowerCase();
  const match = trimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);

  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  return detectUnit(unitStr);
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

/**
 * Parseia string para número, detectando unidade e convertendo se necessário
 */
function parseVolumeString(input: string, targetVariant: VolumeUnit): number | null {
  const trimmed = input.trim().toLowerCase();

  // Extrai número e possível unidade
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const [, numStr, unitStr] = match;

  // Normaliza número
  const numValue = parseNumber(numStr);
  if (numValue === null) {
    return null;
  }

  // Se tem unidade no input, converte se necessário
  const cleanUnitStr = unitStr.trim();
  if (cleanUnitStr) {
    const detectedUnit = detectUnit(cleanUnitStr);
    if (detectedUnit && detectedUnit !== targetVariant) {
      // Converte da unidade detectada para a target
      return convertVolume(detectedUnit, targetVariant, numValue);
    }
  }

  return numValue;
}

/**
 * Parseia string numérica considerando formatos brasileiro e americano
 */
function parseNumber(numStr: string): number | null {
  // Remove espaços
  numStr = numStr.replace(/\s/g, "");

  const lastComma = numStr.lastIndexOf(",");
  const lastDot = numStr.lastIndexOf(".");
  const hasComma = lastComma !== -1;
  const hasDot = lastDot !== -1;

  if (hasComma && hasDot) {
    if (lastComma > lastDot) {
      // Brasileiro: 1.000,50
      numStr = numStr.replace(/\./g, "").replace(",", ".");
    } else {
      // Americano: 1,000.50
      numStr = numStr.replace(/,/g, "");
    }
  } else if (hasComma && !hasDot) {
    // Só vírgulas: detecta se é milhar ou decimal
    const afterComma = numStr.split(",").slice(1);
    const isThousandSep = afterComma.every((part) => part.length === 3);
    if (isThousandSep) {
      numStr = numStr.replace(/,/g, "");
    } else {
      numStr = numStr.replace(",", ".");
    }
  } else if (!hasComma && hasDot) {
    // Só pontos: múltiplos pontos = milhar brasileiro
    const afterDot = numStr.split(".").slice(1);
    if (afterDot.length > 1) {
      numStr = numStr.replace(/\./g, "");
    }
  }

  const value = parseFloat(numStr);
  return isNaN(value) ? null : value;
}

/**
 * Detecta unidade a partir de string (símbolo ou alias)
 */
function detectUnit(unitStr: string): VolumeUnit | null {
  const normalized = unitStr.toLowerCase().trim();

  // Checa aliases primeiro
  if (normalized in VOLUME_ALIASES) {
    return VOLUME_ALIASES[normalized] as VolumeUnit;
  }

  // Checa símbolos e nomes
  for (const [variant, config] of Object.entries(VOLUME_UNITS)) {
    if (
      config.symbol.toLowerCase() === normalized ||
      config.singular?.toLowerCase() === normalized ||
      config.plural?.toLowerCase() === normalized ||
      variant.toLowerCase() === normalized
    ) {
      return variant as VolumeUnit;
    }
  }

  return null;
}
